import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "./App.css";
import { AppRoute } from "./types/routes";
import { useNavigation } from "./hooks/useNavigation";
import AppRoutes from "./routes/AppRoutes";
import { useDispatch } from "react-redux";
import { logout as clearAuthState } from "./store/features/auth/authSlice";
import { useAuthApi } from "./api/useAuthApi";
import Toast from "./components/uiComponents/Toast";
import { AuthUser } from "./types/auth-types";
import FirebasePageTracker from "./components/analytics/FirebasePageTracker";

import { Analytics } from "@vercel/analytics/react";
import {
  clearAnalyticsUser,
  identifyAnalyticsUser,
  trackEvent,
} from "./utils/firebase-analytics";
import {
  getCheckoutPlanFromSearch,
  getPendingCheckoutPlan,
  setPendingCheckoutPlan,
} from "./utils/pending-checkout-plan";

function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigation = useNavigation();
  const location = useLocation();
  const dispatch = useDispatch();
  const [toast, setToast] = useState<{
    message: string;
    type: "error";
    id: number;
  } | null>(null);

  const showToast = useCallback(() => {
    setToast({ message: "Session Expired", type: "error", id: Date.now() });
  }, []);

  const { fetchMe } = useAuthApi(showToast);

  const removeLoadingScreen = () => {
    const loadingScreen = document.getElementById("loading-screen");
    if (loadingScreen) {
      loadingScreen.style.opacity = "0";
      setTimeout(() => loadingScreen.remove(), 500);
    }
  };

  useEffect(() => {
    removeLoadingScreen();
  }, []);

  useEffect(() => {
    const requestedPlan = getCheckoutPlanFromSearch(location.search);
    if (requestedPlan) {
      setPendingCheckoutPlan(requestedPlan);
    }
  }, [location.search]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      clearAnalyticsUser();
      dispatch(clearAuthState());
      navigation.replace(AppRoute.LOGIN);
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, [dispatch, navigation]);

  useEffect(() => {
    const isAuthCallbackPage = window.location.pathname.startsWith(
      AppRoute.AUTH_CALLBACK
    );

    const initSession = async () => {
      const me = await fetchMe();
      const userData = me?.data?.user;

      if (userData) {
        localStorage.setItem("account_details", JSON.stringify(userData));
        identifyAnalyticsUser(userData.id, {
          auth_provider: "supabase",
        });
        setUser({
          id: userData.id,
          email: userData.email,
          app_metadata: {},
          user_metadata: {},
          aud: "authenticated",
          created_at: new Date().toISOString(),
        });
      }

      setLoading(false);
    };

    if (isAuthCallbackPage) {
      setLoading(false);
      return;
    } else {
      initSession();
    }
  }, [fetchMe]);

  const handleAuthSuccess = (authenticatedUser: AuthUser) => {
    setUser(authenticatedUser);
    identifyAnalyticsUser(authenticatedUser.id, {
      auth_provider: "supabase",
    });
    trackEvent("login_completed");
    const requestedPlan =
      getCheckoutPlanFromSearch(location.search) ?? getPendingCheckoutPlan();
    if (requestedPlan) {
      navigation.replace(`${AppRoute.HOME}?plan=${requestedPlan}`);
      return;
    }
    navigation.replace(AppRoute.DASHBOARD);
  };

  const handleLogout = () => {
    setUser(null);
    clearAnalyticsUser();
    dispatch(clearAuthState());
    navigation.replace(AppRoute.LOGIN);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-lg text-gray-500">
        Loading...
      </div>
    );
  }

  return (
    <div className="App">
      <FirebasePageTracker />
      <Analytics />

      <AppRoutes
        user={user}
        onAuthSuccess={handleAuthSuccess}
        onLogout={handleLogout}
      />
      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
    </div>
  );
}

export default App;
